import type { NavItem } from '@agentic-flow-studio/shared/types/command-palette';
import {
  Briefcase,
  CalendarDays,
  Contact,
  Layers,
  MapPin,
  Route,
  Settings,
  Sliders,
  UserCircle,
  Users,
  Wrench,
} from 'lucide-react';

/**
 * Every navigable destination in Job Plan, in the order the sidebar renders them.
 *
 * Single source of truth for both `AppSidebar` and the Ctrl/Cmd+K command palette — adding a
 * route here lights it up in both places at once. Order matters: the palette promotes the
 * leading entries to its root view, so keep the day-to-day surfaces first.
 */
export function buildNavItems(orgId: string, workspaceId: string): NavItem[] {
  const base = `/org/${orgId}/workspace/${workspaceId}`;
  const settingsBase = `${base}/settings`;

  return [
    {
      id: 'schedule',
      title: 'Schedule Board',
      icon: Route,
      url: `${base}/schedule`,
      section: 'Workspace',
      keywords: ['schedule', 'dispatch', 'routes', 'timeline', 'board', 'planner'],
      isActive: (pathname) => pathname.startsWith(`${base}/schedule`),
    },
    {
      id: 'calendar',
      title: 'Calendar',
      icon: CalendarDays,
      url: `${base}/calendar`,
      section: 'Workspace',
      keywords: ['calendar', 'events', 'schedule', 'dates'],
      isActive: (pathname) => pathname.startsWith(`${base}/calendar`),
    },
    {
      id: 'jobs',
      title: 'Jobs',
      icon: Briefcase,
      url: `${base}/jobs`,
      section: 'Workspace',
      keywords: ['jobs', 'work orders', 'tickets', 'tasks'],
      isActive: (pathname) => pathname.startsWith(`${base}/jobs`),
    },
    {
      id: 'contacts',
      title: 'Contacts',
      icon: Contact,
      url: `${base}/contacts`,
      section: 'Workspace',
      keywords: ['people', 'customers', 'clients', 'addresses'],
      isActive: (pathname) => pathname.startsWith(`${base}/contacts`),
    },
    {
      id: 'operators',
      title: 'Operators',
      icon: Users,
      url: `${base}/operators`,
      section: 'Workspace',
      keywords: ['operators', 'technicians', 'drivers', 'team', 'field'],
      isActive: (pathname) => pathname.startsWith(`${base}/operators`),
    },
    {
      id: 'skills',
      title: 'Skills',
      icon: Wrench,
      url: `${base}/skills`,
      section: 'Workspace',
      keywords: ['skills', 'qualifications', 'trades', 'certifications'],
      isActive: (pathname) => pathname.startsWith(`${base}/skills`),
    },
    {
      id: 'locations',
      title: 'Locations',
      icon: MapPin,
      url: `${base}/locations`,
      section: 'Workspace',
      keywords: ['locations', 'depots', 'addresses', 'sites', 'places'],
      isActive: (pathname) => pathname.startsWith(`${base}/locations`),
    },
    {
      id: 'job-plan-settings',
      title: 'Job Plan Settings',
      icon: Sliders,
      url: `${settingsBase}/job-plan`,
      section: 'Settings',
      keywords: ['rules', 'vertical', 'depot', 'service window', 'defaults'],
      isActive: (pathname) => pathname.startsWith(`${settingsBase}/job-plan`),
    },
    {
      id: 'contact-types',
      title: 'Contact Types',
      icon: Layers,
      url: `${settingsBase}/contact-types`,
      section: 'Settings',
      keywords: ['custom fields', 'schema'],
      isActive: (pathname) => pathname.startsWith(`${settingsBase}/contact-types`),
    },
    {
      // The sidebar swaps this icon for the signed-in user's avatar; the palette uses it as-is.
      id: 'profile',
      title: 'Profile',
      icon: UserCircle,
      url: `${base}/profile`,
      section: 'Account',
      keywords: ['me', 'my account', 'avatar'],
      isActive: (pathname) => pathname.startsWith(`${base}/profile`),
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      url: `${base}/settings`,
      section: 'Account',
      keywords: ['preferences', 'workspace', 'members', 'billing'],
      isActive: (pathname) =>
        pathname.startsWith(`${base}/settings`) &&
        !pathname.startsWith(`${settingsBase}/job-plan`) &&
        !pathname.startsWith(`${settingsBase}/contact-types`),
    },
  ];
}
